import type { CourseWithoutBlocks } from '@/types/course'
import type { ContentLang } from '../editorSlice'
import { hasTranslationRow, translationLangOf } from './courseTranslation'
import { seedLangFor } from '../components/studio/seedContentLang'
import { messagesNow } from '@/i18n'
import { messages } from './messages'
import type { BlockIssue } from './publishBlockIssues'

const LANG_CODE = { ru: 'RU', en: 'EN' } as const

/** Незаполненное поле курса: что и на каком языке. */
export interface CourseFieldIssue {
  field: string
  lang: string
}

/** Незаполненное поле урока. `lesson` — номер с единицы, как на плашке урока. */
export interface LessonFieldIssue extends CourseFieldIssue {
  lesson: number
  lessonId: number
}

// Проверки экранов урока живут рядом (файл стал бы больше лимита) — реэкспорт,
// чтобы у вызывающих был один модуль «что мешает публикации».
export { lessonBlockIssues, overflowSlot, type BlockIssue } from './publishBlockIssues'

/**
 * Что не заполнено у самого курса: название и описание на языке оригинала и,
 * если у курса заведён второй язык, на нём тоже — без этого курс не публикуется.
 */
export const courseFieldIssues = (
  course: CourseWithoutBlocks | undefined,
  startedRun: boolean,
): CourseFieldIssue[] => {
  if (!course) return []
  const langs = requiredLangs(course, startedRun)
  const m = messagesNow(messages)
  const issues: CourseFieldIssue[] = []
  // Курс без уроков публиковать нечего — ученик открыл бы пустую карту.
  if ((course.lessons ?? []).length === 0) issues.push({ field: m.noLessons, lang: '—' })
  for (const lang of langs) {
    const name = (lang === 'en' ? course.nameEn : course.name) ?? ''
    const description = (lang === 'en' ? course.descriptionEn : course.description) ?? ''
    if (!name.trim()) issues.push({ field: m.fieldName, lang: LANG_CODE[lang] })
    if (!description.trim()) issues.push({ field: m.fieldDescription, lang: LANG_CODE[lang] })
  }
  return issues
}

/** Языки, на которых курс обязан быть заполнен: оригинал (первым) и заведённый второй. */
export const requiredLangs = (course: CourseWithoutBlocks, startedRun: boolean): ContentLang[] => {
  const langs: ContentLang[] = [seedLangFor(course)]
  if (hasTranslationRow(course, startedRun)) langs.push(translationLangOf(course))
  return langs
}

/** Название и описание каждого урока на тех же языках, что и курс. */
export const lessonFieldIssues = (
  course: CourseWithoutBlocks | undefined,
  startedRun: boolean,
): LessonFieldIssue[] => {
  if (!course) return []
  const langs = requiredLangs(course, startedRun)
  const m = messagesNow(messages)
  return (course.lessons ?? []).flatMap((lesson, rank) => [
    // Урок без единого блока курс тоже не пропустит — счётчик есть в данных
    // курса, поэтому кнопка гаснет сразу, без загрузки блоков.
    ...(lesson.blocksCount === 0
      ? [{ lesson: rank + 1, lessonId: lesson.id, field: m.noBlocks, lang: '—' }]
      : []),
    ...langs.flatMap((lang) => {
      const at = { lesson: rank + 1, lessonId: lesson.id, lang: LANG_CODE[lang] }
      const name = (lang === 'en' ? lesson.nameEn : lesson.name) ?? ''
      const description = (lang === 'en' ? lesson.descriptionEn : lesson.description) ?? ''
      return [
        ...(name.trim() ? [] : [{ ...at, field: m.fieldName }]),
        ...(description.trim() ? [] : [{ ...at, field: m.fieldDescription }]),
      ]
    }),
  ])
}

/** Итог урока в карте родителя: `null` — снова «проверяется», запись удаляется. */
export const withLessonResult = (
  prev: Record<number, BlockIssue[]>,
  lessonId: number,
  issues: BlockIssue[] | null,
): Record<number, BlockIssue[]> => {
  if (issues !== null) return { ...prev, [lessonId]: issues }
  const next = { ...prev }
  delete next[lessonId]
  return next
}

/**
 * Текст отказа повысить доступ курсу, где есть переполненный составной блок
 * (проверка клиентская — api её не делает). Места «Урок N, экран M», язык
 * в скобках только у EN, без повторов; `null` — переполнений нет.
 */
export const designOverflowRefusal = (issues: BlockIssue[]): string | null => {
  const m = messagesNow(messages)
  const places = [
    ...new Set(
      issues
        .filter((i) => i.screen !== null && !i.warning)
        .map((i) => m.designOverflowPlace(i.lesson, i.screen as number, i.lang)),
    ),
  ]
  return places.length === 0 ? null : m.designOverflowRefusal(places)
}

/** Код языка из таблицы («RU»/«EN»; «—» у урока без блоков) → язык правки. */
export const langOf = (code: string) =>
  code === 'EN' ? ('en' as const) : code === 'RU' ? ('ru' as const) : undefined
